import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Box, Button, css, Stack, styled, Typography, useMediaQuery, useTheme } from '@mui/material'
import DOMPurify from 'dompurify'
import type { FC } from 'react'
import { useTranslation } from 'react-i18next'

import { COLORS, ICONS, LinkUnstyled, optimizelyCss, unit, Z_INDEX } from '@hcr/ui'

import { SvgHoldingsNotVisible } from '../../../assets/images'
import { useIdToken } from '../../../hooks/api/b2c'
import { useHoldingsNotVisibleQuery } from '../../../hooks/api/consumer/queries'
import { Path } from '../../../models/navigation'
import { to } from '../../../utils/navigation'
import { LayoutNavigation } from '../../common/layout-navigation'
import { LayoutNavigationBackError500 } from '../../common/layout-navigation-back'

import { OwnerHoldingsNotVisibleLoading } from './OwnerHoldingsNotVisibleLoading'

const BACK_NAVIGATION_PATH = to(Path.Owner)

export const OwnerHoldingsNotVisible: FC = () => {
  const { t } = useTranslation()
  const idToken = useIdToken()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))

  const holdingsNotVisible = useHoldingsNotVisibleQuery({ idToken: String(idToken) })

  if (holdingsNotVisible.isError) {
    return <LayoutNavigationBackError500 to={BACK_NAVIGATION_PATH} />
  }

  if (holdingsNotVisible.isSuccess) {
    return (
      <LayoutNavigation showSideNavigation={false}>
        <Stack>
          <ArrowLinkIcon to={Path.Owner} aria-label={t('navigation.back')}>
            <Stack paddingX={unit(3)} paddingY={unit(2)} direction={'row'} alignItems={'center'}>
              <FontAwesomeIcon style={{ padding: unit(3) }} icon={ICONS.farArrowLeft} />
              <Typography component='span' variant='titleM'>
                {t('navigation.back')}
              </Typography>
            </Stack>
          </ArrowLinkIcon>
          <Stack
            direction='column'
            paddingLeft={unit(isMobile ? 6.5 : 25)}
            paddingRight={unit(isMobile ? 8 : 25)}
            gap={unit(4.5)}
          >
            <Stack
              direction='column'
              gap={unit(3)}
              alignItems='center'
              justifyContent='center'
              textAlign='center'
              paddingLeft={unit(isMobile ? 0 : 17.5)}
              paddingRight={unit(isMobile ? 0 : 20)}
            >
              <SvgHoldingsNotVisible width={unit(41.25)} height={unit(39)} />
              <Typography variant='headlineL'>{holdingsNotVisible.data.title}</Typography>
              <Typography variant='labelM'>{holdingsNotVisible.data.ingress}</Typography>
            </Stack>
            <Typography
              paddingLeft={unit(isMobile ? 0 : 6.5)}
              paddingRight={isMobile ? 0 : unit(8)}
              variant='bodyM'
              css={[optimizelyCss, holdingDescriptionCss]}
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(holdingsNotVisible.data.content) }}
            />
          </Stack>
          <Stack
            direction='column'
            paddingTop={unit(8)}
            paddingX={unit(isMobile ? 7.5 : 32.5)}
            paddingBottom={unit(isMobile ? 10 : 20)}
          >
            <Button variant='outlined' color='inherit' component={LinkUnstyled} to={Path.Owner}>
              <Box sx={{ marginRight: unit(2.5) }}>
                <FontAwesomeIcon icon={ICONS.fasArrowLeft} />
              </Box>
              {t('navigation.back')}
            </Button>
          </Stack>
        </Stack>
      </LayoutNavigation>
    )
  }

  return <OwnerHoldingsNotVisibleLoading />
}

const ArrowLinkIcon = styled(LinkUnstyled)`
  height: ${unit(16)};
  display: inline-flex;
  align-items: center;
  justify-content: start;
  position: relative;
  z-index: ${Z_INDEX.navigation};
  font-size: ${unit(6)};
`

const holdingDescriptionCss = css`
  & ul {
    list-style: none;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: ${unit(3)};
  }

  & li {
    font-family: 'ABC Ginto Nord', sans-serif;
    font-size: ${unit(3.5)};
    line-height: ${unit(6)};
    letter-spacing: ${unit(0.1)};
  }

  & a {
    color: ${COLORS.burgundi[500]};
    text-decoration: underline;
    font-weight: bold;
  }

  & li::before {
    content: '';
    display: inline-block;
    width: ${unit(1.5)};
    height: ${unit(1.5)};
    background-color: ${COLORS.burgundi[500]};
    margin-right: ${unit(3.5)};
    vertical-align: middle;
  }
`
